import { useState, useEffect } from 'react';


/**
 * Manages the data-loading lifecycle for a flowing page section.
 *
 * Encapsulates componentDidMount / componentWillUnmount + listener() from FlowingPage.
 * Subscribes to `data` via addLocalListener on mount, unsubscribes on unmount.
 *
 * @param {object} data          - DataController instance (has addLocalListener, getLocalValue, etc.)
 * @param {object} flowingConfig - { dataName, ... }
 * @returns {{
 *   content, setContent,
 *   pageBreaks, setPageBreaks,
 *   manualPageBreaks, setManualPageBreaks,
 *   pages, setPages,
 *   editorKey,
 * }}
 */
export function useFlowingContent(data, flowingConfig) {
	const [content, setContent] = useState(null);
	const [pageBreaks, setPageBreaks] = useState([]);
	const [manualPageBreaks, setManualPageBreaks] = useState([]);
	const [pages, setPages] = useState(1);
	const [editorKey, setEditorKey] = useState("testkey");

	useEffect(() => {
		function listener() {
			if (!data.localContentLoaded) {
				return new Promise(resolve => resolve(false));
			}

			const dataName = flowingConfig.dataName;
			const contentVal = data.getLocalValue(dataName);
			const storedPageBreaks = data.getLocalValue(dataName + 'PageBreaks');

			const pageBreaksArr = [];
			const manualPageBreaksArr = [];

			if (contentVal && contentVal !== null && contentVal !== "" || flowingConfig.defaultContent) {
				//FIXME editor not handling updating props, so change whole component. BAD! fix in editor.

				const defaultContent = flowingConfig.defaultContent ? flowingConfig.defaultContent : null
				const parsedDefaultContent = defaultContent !== "" && defaultContent !== null ? JSON.parse(defaultContent) : null;
				const parsed = contentVal && (contentVal !== "" && contentVal !== null) ? JSON.parse(contentVal) : parsedDefaultContent;

				const formattedParsed = parsed && parsed.length > 0 ? parsed : [{ type: 'text', children: [{ text: '' }] }];

				try {
					if (storedPageBreaks) {
						JSON.parse(storedPageBreaks).forEach(pb => {
							if (pb.id) {
								manualPageBreaksArr.push(pb);
							} else {
								pageBreaksArr.push(pb);
							}
						});
					}

					if (parsed.pageBreaks) {
						const entityMap = parsed.entityMap;
						parsed.pageBreaks.forEach(pb => {
							if (pb.id) {
								//Sanity check that breaks are actually valid
								if (Object.keys(entityMap).find(key => entityMap[key].data.pageBreakId === pb.pageBreakId)) {
									manualPageBreaksArr.push(pb);
								} else {
									console.log("Rejecting with no matching entity: " + pb.id);
								}
							} else {
								pageBreaksArr.push(pb);
							}
						});
					}
				} catch (e) {
					console.log(e);
				}

				// Strip orphaned node-level pageBreaks entries that have no
			// corresponding state-level manualPageBreaks entry. These ghost
			// entries can accumulate when the content JSON and the separate
			// PageBreaks storage get out of sync (e.g. a break was added to a
			// node but the PageBreaks key was saved without it).
			const manualIds = new Set(manualPageBreaksArr.map(pb => pb.pageBreakId || pb.id));
			function stripOrphanedBreaks(nodes) {
				for (let i = 0; i < nodes.length; i++) {
					const node = nodes[i];
					if (node.pageBreaks && Array.isArray(node.pageBreaks) && node.pageBreaks.length > 0) {
						const kept = node.pageBreaks.filter(pb => manualIds.has(pb.id));
						if (kept.length !== node.pageBreaks.length) {
							nodes[i] = { ...node, pageBreaks: kept };
						}
					}
					if (node.children && Array.isArray(node.children)) {
						stripOrphanedBreaks(node.children);
					}
				}
			}
			stripOrphanedBreaks(formattedParsed);

			const allBreaks = pageBreaksArr.concat(manualPageBreaksArr);
				const pagesRequired = allBreaks.length + 1;
				setContent(formattedParsed);
				setEditorKey(k => k + 1);
				setPageBreaks(pageBreaksArr);
				setManualPageBreaks(manualPageBreaksArr);
				setPages(pagesRequired);
			} else {
				setContent(null);
				setEditorKey(k => k + 1);
				setPageBreaks(pageBreaksArr);
				setManualPageBreaks(manualPageBreaksArr);
			}

			//TODO - trigger the return on setState callback above. Brain fried can't figure out how right now.
			return new Promise(resolve => resolve(true));
		}

		data.addLocalListener(listener);
		listener();

		return () => data.removeLocalListener(listener);
	}, [data, flowingConfig]);

	return {
		content, setContent,
		pageBreaks, setPageBreaks,
		manualPageBreaks, setManualPageBreaks,
		pages, setPages,
		editorKey,
	};
}
